import {create} from 'zustand'

import {
  getPocketBaseAuthHeaders,
  pocketBase,
  subscribeToPocketBaseAuthState,
} from '@/services/pocketbase'
import {identifyPostHog} from '@/services/posthog'

export interface UserInfo {
  id: string
  name: string
  email: string
}

export interface PackagesInfo {
  [index: string]: string
}

export interface RuleInfo {
  kincir?: CommonRules
  dadu?: CommonRules
}

export interface CommonRules {
  duration: Durations
  export: boolean
  time_window?: TimeWindow
}

export interface Durations {
  [index: string]: boolean
}

export interface TimeWindow {
  mode: 'all_day' | 'restricted'
  ranges: TimeWindowRange[]
}

export interface TimeWindowRange {
  start: string
  end: string
  overnight: boolean
}

interface UserStore {
  info: UserInfo | null
  packages: PackagesInfo | null
  rules: RuleInfo | null
  login: () => Promise<void>
  logout: () => void
  googleLogin: () => Promise<void>
  authenticateGoogle: (code: string) => Promise<void>
  syncUserExtendedInfo: () => Promise<void>
}

const authRedirectURL = new URL('/auth', window.location.origin).toString()

export const useUserStore = create<UserStore>((set, get) => ({
  info: null,
  packages: null,
  rules: null,

  async login() {
    const authData = await pocketBase.collection('users').authWithOAuth2({provider: 'google'})
    await updateUserName(authData.meta?.name)
  },

  logout() {
    pocketBase.authStore.clear()
    window.location.reload()
  },

  async googleLogin() {
    const provider = await getGoogleProvider()
    window.localStorage.setItem('provider', JSON.stringify(provider))
    window.localStorage.setItem('lastPageURL', window.location.href)
    window.location.assign(provider.authURL + authRedirectURL)
  },

  async authenticateGoogle(code: string) {
    const provider = JSON.parse(window.localStorage.getItem('provider') ?? '{}')
    const authData = await pocketBase.collection('users').authWithOAuth2Code(
      provider.name,
      code,
      provider.codeVerifier,
      authRedirectURL,
    )

    await updateUserName(authData.meta?.name)

    window.location.assign(window.localStorage.getItem('lastPageURL') ?? '/')
  },

  async syncUserExtendedInfo() {
    const response = await fetch(new URL('/api/v2/user', import.meta.env.VITE_API_URL), {
      headers: getPocketBaseAuthHeaders(),
    })

    if (!response.ok) return

    const userInfo = await response.json() as {packages?: PackagesInfo; rules?: RuleInfo}
    set({packages: userInfo.packages ?? null, rules: userInfo.rules ?? null})
  },
}))

let stopUserStoreAuthSync: (() => void) | null = null

export function initializeUserStore(): () => void {
  if (stopUserStoreAuthSync !== null) return stopUserStoreAuthSync

  stopUserStoreAuthSync = subscribeToPocketBaseAuthState(async ({isValid, model}) => {
    if (isValid && model !== null) {
      const info: UserInfo = {
        id: model.id,
        name: typeof model.name === 'string' ? model.name : '',
        email: typeof model.email === 'string' ? model.email : '',
      }

      useUserStore.setState({info})
      identifyPostHog(info.id, {name: info.name, email: info.email})
    } else {
      useUserStore.setState({info: null, packages: null, rules: null})
    }

    try {
      await useUserStore.getState().syncUserExtendedInfo()
    } catch (error) {
      console.error('Unable to synchronize extended user information', error)
    }
  })

  return stopUserStoreAuthSync
}

export function cleanupUserStore(): void {
  stopUserStoreAuthSync?.()
  stopUserStoreAuthSync = null
}

async function getGoogleProvider() {
  const authMethods = await pocketBase.collection('users').listAuthMethods()
  const provider = authMethods.oauth2.providers.find(({name}) => name === 'google')

  if (authMethods.oauth2.enabled && provider !== undefined) return provider

  throw new Error('Google provider not found')
}

async function updateUserName(name: unknown): Promise<void> {
  const userModel = pocketBase.authStore.model

  if (userModel === null || userModel.name !== '' || typeof name !== 'string') return

  await pocketBase.collection('users').update(userModel.id, {name})
}
